import React, { useEffect, useRef, useState } from "react";
import { Grid, Stack } from "@mui/material";
import { AutocompleteField, Box, Button, Card, Form, FormControlLabel, MenuItem, SelectField, Switch, Typography } from '@curves/ui-base';

import { PointsRedeemTable } from "@Local/Points/Redeem";
import { PointsDialogBox } from "@Local/Points/Dialog/";
import { PointsProvider, usePointsContext } from "@Local/Points/Components/PointsContext";
import { initBookmark } from "@Local/Points/staticData";
import usePointsRewardsApi from "@Apis/usePointsRewardsApi";
import { isEmpty, isInvalid, refIsRequiredError } from "@Util/utils";
import { head } from "lodash/array";
const SearchBar = React.memo(props => {
	const [options, setOptions] = useState({});
	const [clubID, setClubID] = useState(0);
	const clubIDRef = useRef(null);
	const peopleIdRef = useRef(null);
	const activityIdRef = useRef(null);
	const { setSourceData, setActivityIdRef, setRefreshRef, bookmark, setClubIDRef, setOptionsRef, dialogRef, setBookmarkNum } = usePointsContext();
	const { getPointsRewardsOverviewOptionsApi, getPointsRewardsOverviewApi, getPointRewardsExportApi, getPointRewardsExportAllApi } = usePointsRewardsApi();
	const { peopleOption = [], activityOption = [], clubOption = [] } = options;
	const prepareToResetRef = useRef(null);
	const preloadRef = useRef(false);
	const getParams = (pageModel = {}) => {
		let defaultActivityID = (head(activityOption) ?? { value: "" }).value;
		let params = {
			clubID: clubIDRef.current.getResult("value"),
			activityID: activityIdRef.current.getResult() || defaultActivityID,
			rewardID: peopleIdRef.current?.getResult("id") ?? "",
			isPrepareToReset: prepareToResetRef.current?.checked ?? true,
			...bookmark,
		};
		if (!isEmpty(pageModel)) {
			Object.assign(params, pageModel);
		}
		return params;
	};
	const handleSearchOnClick = () => {
		if (isEmpty(options) || isEmpty(clubID)) return;
		if (!refIsRequiredError(clubIDRef, peopleIdRef, activityIdRef)) {
			setBookmarkNum(1);
			getOverviewApi(getParams());
		}
	};
	const handleClubOnChange = (targetData = {}) => {
		let value;
		if (isInvalid(targetData)) {
			value = "";
			setSourceData({});
		} else {
			value = targetData.value;
		}
		setClubID(value);
		setClubIDRef(value);
	};
	const handleRedeemAddMemberOnclick = () => {
		const params = getParams();
		dialogRef.current.openRedeemAddMember({ clubID: params.clubID, activityID: params.activityID }, handleSearchOnClick, clubOption);
	};
	const handleExcelOnclick = () => {
		const params = getParams();
		getExcelApiDownloadApi({ clubID: params.clubID, activityID: params.activityID });
	};
	const handleAllExcelOnclick = () => {
		if (!refIsRequiredError(peopleIdRef, activityIdRef)) {
			const params = getParams();
			getExcelApiDownloadAllApi({ activityID: params.activityID });
		}
	};
	// 總覽查詢
	const getOverviewApi = async params => {
		const resp = await getPointsRewardsOverviewApi(params);
		if (resp) {
			const { list, ...pageModel } = resp;
			setSourceData({ list: [...list], ...pageModel });
			setActivityIdRef(params.activityID);
			preloadRef.current = true;
		}
	};
	const getExcelApiDownloadApi = async params => {
		await getPointRewardsExportApi(params);
	};
	const getExcelApiDownloadAllApi = async params => {
		await getPointRewardsExportAllApi(params);
	};
	// 切換店舖
	const getChangeClubOptions = async () => {
		setBookmarkNum(1);
		const resp = await getPointsRewardsOverviewOptionsApi({ clubID: clubID });
		if (resp) {
			setOptions(resp);
			setOptionsRef(resp);
			preloadRef.current = true;
		}
	};
	const initOptions = async () => {
		const resp = await getPointsRewardsOverviewOptionsApi();
		if (resp) {
			setOptions(resp);
			preloadRef.current = true;
		}
	};
	useEffect(
		() => {
			if (preloadRef.current) {
				preloadRef.current = false;
				const params = getParams(bookmark);
				getOverviewApi(params);
			}
		},
		// eslint-disable-next-line
		[bookmark.pageIndex]
	);
	useEffect(
		() => {
			if (preloadRef.current) {
				getChangeClubOptions();
			}
		},
		// eslint-disable-next-line
		[clubID]
	);
	useEffect(
		() => {
			if (preloadRef.current) {
				handleSearchOnClick();
			}
		},
		// eslint-disable-next-line
		[options, clubID]
	);
	useEffect(
		() => {
			if (isEmpty(options)) {
				initOptions();
			}
		},
		// eslint-disable-next-line
		[]
	);
	setRefreshRef(() => {
		getOverviewApi(getParams(bookmark));
	});
	return (
		<Form onSubmit={handleSearchOnClick}>
			<Card>
				<Card.CardContent className="p-4 px-2">
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<AutocompleteField
								ref={clubIDRef}
								label="店舖名稱"
								inputProps={{ placeholder: "請輸入店名/店碼" }}
								options={clubOption}
								onChange={handleClubOnChange}
								optionKeys={["text"]}
								primaryKey={"id"}
								direction="row"
								required
								fullWidth
							/>
						</Grid>
						<Grid item xs={6}>
							<SelectField key={activityOption.length} ref={activityIdRef} label="兌換時間" defaultValue={activityOption[0]?.value ?? ""} direction="row" displayEmpty required fullWidth>
								<MenuItem value="" disabled>
									<em>請選擇</em>
								</MenuItem>
								{activityOption.map(({ text, value, disabled }) => (
									<MenuItem key={value} value={value} disabled={disabled}>
										{text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
						{!!clubID ? (
							<React.Fragment>
								<Grid item xs={6}>
									<AutocompleteField
										label="兌換對象"
										inputProps={{ placeholder: "請輸入姓名/手機號碼/會員編號(擇一)" }}
										ref={peopleIdRef}
										options={peopleOption}
										optionKeys={["name", "mobile", "no"]}
										primaryKey={"id"}
										direction="row"
										fullWidth
									/>
								</Grid>
								<Grid item xs={6}>
									<Stack direction="row" spacing={2}>
										<FormControlLabel
											className={"m-0"}
											control={<Switch inputProps={{ ref: prepareToResetRef }} defaultChecked={false} />}
											prefixLabel={"預計歸零"}
											labelPlacement={"start"}
										/>
										<Button variant="contained" color="primary" type="submit">
											搜尋
										</Button>
										<Button variant="contained" color="info" onClick={handleRedeemAddMemberOnclick}>
											新增兌換會員
										</Button>
										<Button variant="contained" color="success" onClick={handleExcelOnclick}>
											下載Excel
										</Button>
									</Stack>
								</Grid>
							</React.Fragment>
						) : (
							<Grid item xs={4}>
								<Button className="ml-2" variant="contained" color="success" onClick={handleAllExcelOnclick}>
									下載全店舖Excel
								</Button>
							</Grid>
						)}
					</Grid>
				</Card.CardContent>
			</Card>
		</Form>
	);
});
// 點數兌換
const PointsRedeemPage = React.forwardRef((props, ref) => {
	const [sourceData, setSourceData] = useState({});
	const dialogRef = useRef(null);
	const activityIdRef = useRef(null);
	const optionsRef = useRef(null);
	const clubIDRef = useRef(null);
	const refreshRef = useRef(null);
	const [bookmark, setBookmark] = useState({
		...initBookmark(),
	});
	const childContext = {
		dialogRef: dialogRef,
		setDialogRef: el => (dialogRef.current = el),
		activityId: activityIdRef,
		setActivityIdRef: actID => (activityIdRef.current = actID),
		clubIDFn: () => clubIDRef.current,
		setClubIDRef: clubID => (clubIDRef.current = clubID),
		optionsFn: () => optionsRef.current,
		setOptionsRef: (opts = {}) => (optionsRef.current = opts),
		sourceData,
		setSourceData,
		bookmark: bookmark,
		setBookmarkNum: num => setBookmark(pre => ({ ...pre, pageIndex: num })),
		refresh: () => typeof refreshRef.current === "function" && refreshRef.current(),
		setRefreshRef: fn => (refreshRef.current = fn),
	};
	return (
		<PointsProvider value={childContext}>
			<Box className="container main-container-spacing points-redeem-container">
				<Box className={"page-Box"}>
					<Typography className={"page-header-title"} variant={"h3"}>
						會員點數總覽
					</Typography>
				</Box>
				<SearchBar />
				<PointsRedeemTable />
				<PointsDialogBox ref={dialogRef} />
			</Box>
		</PointsProvider>
	);
});
export default PointsRedeemPage;
